import classnames from 'classnames';
import isArray from 'lodash-es/isArray';
import isEqual from 'lodash-es/isEqual';
import isObject from 'lodash-es/isObject';
import isString from 'lodash-es/isString';
import map from 'lodash-es/map';
import merge from 'lodash-es/merge';
import sum from 'lodash-es/sum';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import { Icon, Label, Pagination, Table } from 'semantic-ui-react';
import * as customPropTypes from 'semantic-ui-react/src/lib/customPropTypes';
import { formatDateTime } from '../../dates';
import IconLabel from '../IconLabel';
import Loading from '../Loading';

const defaultDefs = {
	header: {
		key: 'header',
		// TODO propose inverted/colored rows
		className: classnames('inverted', 'light', 'teal'),
		cells: {
			textAlign: 'center'
		}
	},
	footer: {
		key: 'footer',
		cells: {}
	},
	pagination: {
		key: 'pagination',
		cells: {
			textAlign: 'right'
		}
	}
};

const applyHeaderCellsDefault = (cells, cellDef) =>
	cells.map(h =>
		isString(h)
			? {
					...cellDef,
					key: h,
					content: h
			  }
			: {
					...cellDef,
					key: h.content,
					...h
			  }
	);

const getColumnCount = (headerRow, { cells, ...headerRowDefault }) => {
	// ['col1', 'col2' ...]
	// [ {colSpan: 3, content: col1} ]
	let headerDef;

	if (isObject(headerRow) && !isArray(headerRow) && headerRow.cells) {
		// can be an extended definition like this
		// { cells: [{colSpan: 3, content: col1}] }

		headerDef = {
			...headerRowDefault,
			...headerRow,
			cells: applyHeaderCellsDefault(headerRow.cells, cells || {})
		};
	} else {
		// it's an array of cells or of strings
		headerDef = {
			...headerRowDefault,
			cells: applyHeaderCellsDefault(headerRow, cells || {})
		};
	}
	return [
		headerDef,
		sum(map(headerDef.cells, col => (isString(col) ? 1 : col.colSpan || 1)))
	];
};

const MemoTable = memo(
	Table,
	(
		{ tableData, headerRow },
		{ tableData: newTableData, headerRow: newHeaderRow }
	) =>
		isEqual(
			{
				tableData,
				headerRow
			},
			{
				newTableData,
				newHeaderRow
			}
		)
);

const CustomTable = ({
	defaults,
	dataEmpty,
	dataError,
	tableData,
	renderBodyRow,
	headerRow,
	pagination,
	onPageChange,
	displayTotal,
	isLoading,
	className,
	canExport,
	canRefresh,
	extraButtons,
	print,
	...others
}) => {
	const [userData, setUserData] = useState(tableData);
	const [hasNoData, setHasNoData] = useState(
		!tableData || tableData.length === 0
	);
	const [allowRefresh] = useState(
		pagination && onPageChange && canRefresh !== false
	);
	useEffect(() => {
		if (!tableData || tableData.length === 0) {
			setHasNoData(true);
			setUserData(tableData ? [dataEmpty] : [dataError]);
		} else {
			setUserData(tableData);
			setHasNoData(false);
		}
	}, [tableData]);

	const { header: headerDefault } = merge(defaultDefs, defaults || {});
	const [headerDef, columnCount] = getColumnCount(headerRow, headerDefault);
	const tableProps = {
		...others,
		className: classnames(className, 'custom'),
		headerRow: headerDef,
		tableData: userData,
		renderBodyRow: hasNoData
			? data => ({
					key: 'no-data',
					textAlign: 'center',
					cells: [
						{
							key: 'no-data-text',
							colSpan: columnCount,
							content: allowRefresh ? (
								<Label
									color={tableData ? 'grey' : 'red'}
									content={data}
									icon="refresh"
									onClick={() => onPageChange({}, { ...pagination })}
								/>
							) : (
								<b>{data}</b>
							)
						}
					]
			  })
			: renderBodyRow
	};

	tableProps.tableData = userData;
	return (
		<div className="custom-table__wrapper">
			<Loading isLoading={isLoading} />
			<MemoTable {...tableProps} />
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}
			>
				{canExport && !hasNoData ? (
					<IconLabel
						color="green"
						content={null}
						icon="file excel"
						onClick={() =>
							onPageChange(
								{},
								{
									...pagination,
									exporting: true
								}
							)
						}
					/>
				) : (
					undefined
				)}
				{allowRefresh && !hasNoData ? (
					<IconLabel
						color="red"
						content={null}
						icon="refresh"
						onClick={() => onPageChange({}, { ...pagination })}
					/>
				) : (
					undefined
				)}

				<div
					style={{
						padding: '0 1em',
						flex: 1
					}}
				>
					{extraButtons ? (
						extraButtons({
							...tableProps,
							hasNoData
						})
					) : (
						<>&nbsp;</>
					)}
				</div>
				{displayTotal ? (
					<div>
						{' '}
						<Label
							className="dark"
							color="grey"
							content={displayTotal === true ? 'Total' : displayTotal}
							detail={pagination ? pagination.totalElements : tableData.length}
						/>{' '}
					</div>
				) : (
					undefined
				)}
				{pagination &&
				pagination.totalPages > 0 &&
				pagination.currentPage >= 0 ? (
					<Pagination
						pointing
						secondary
						ellipsisItem={{
							content: <Icon name="ellipsis horizontal" />,
							icon: true
						}}
						firstItem={{
							content: <Icon name="angle double left" />,
							icon: true
						}}
						lastItem={{
							content: <Icon name="angle double right" />,
							icon: true
						}}
						prevItem={{
							content: <Icon name="angle left" />,
							icon: true
						}}
						nextItem={{
							content: <Icon name="angle right" />,
							icon: true
						}}
						activePage={pagination.currentPage + 1}
						totalPages={pagination.totalPages}
						onPageChange={onPageChange}
					/>
				) : (
					undefined
				)}
			</div>
		</div>
	);
};

CustomTable.defaultProps = {
	dataEmpty: 'No data',
	dataError: 'Error retrieving data',
	pagination: null,
	onPageChange: null,
	headerRow: false,
	tableData: [],
	isLoading: false,
	className: null,
	displayTotal: false,
	defaults: {},
	canRefresh: true,
	canExport: false,
	extraButtons: undefined
};

CustomTable.propTypes = {
	defaults: PropTypes.shape({
		header: PropTypes.shape({
			cells: PropTypes.shape({})
		}),
		pagination: PropTypes.shape({
			cells: PropTypes.shape({})
		})
	}),
	dataEmpty: PropTypes.string,
	dataError: PropTypes.string,
	className: PropTypes.string,
	renderBodyRow: PropTypes.func.isRequired,
	headerRow: customPropTypes.itemShorthand,
	pagination: PropTypes.shape({
		totalElements: PropTypes.number,
		totalPages: PropTypes.number,
		size: PropTypes.number,
		currentPage: PropTypes.number
	}),
	extraButtons: PropTypes.element,
	canRefresh: PropTypes.bool,
	canExport: PropTypes.bool,
	onPageChange: customPropTypes.every([
		customPropTypes.demand(['pagination']),
		PropTypes.func
	]),
	tableData: customPropTypes.every([
		customPropTypes.disallow(['children']),
		customPropTypes.demand(['renderBodyRow']),
		PropTypes.array
	]),
	isLoading: PropTypes.bool,
	displayTotal: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
};

export const clickPrint = (id, text, close, title) => e => {
	e.stopPropagation();
	// let { target } = e;
	// while (target.parentElement && target.parentElement.tagName !== 'TABLE') {
	//   target = target.parentElement;
	// }

	// if (!target.parentElement) {
	//
	// } else {
	//   target = target.parentElement;
	const w = window.open('');
	const target = document.getElementById(id);
	if (!target) {
		throw new Error('Unexpected error');
	}

	w.document.write(target.outerHTML);
	w.document.write(`
   <p>
      Report generated on ${formatDateTime(new Date())}
   </p>
   <p>${text}</p>
   `);
	w.document.write(`
<style rel="text/css" >
  table {
      font-family: "Courier New", Courier, mono, monospace;
      font-size: 0.9em;
      width: 100%;border-collapse: collapse;
  }

  th {
      background-color: #00796b;
      color: white;
  }

  tr:nth-child(even) {background-color: #f2f2f2;}

  th, td {
      border-bottom: 1px solid #ddd;
  }

  th {
      height: 50px;
  }
  .print-hidden{
    display: none;
  }
  .collapsing {
    width: 1px;
    white-space: nowrap;
  }

.ui.table.left.aligned,
.ui.table .left.aligned{
  text-align: left;
}
.ui.table.center.aligned,
.ui.table .center.aligned{
  text-align: center;
}
.ui.table.right.aligned,
.ui.table .right.aligned{
  text-align: right;
}



.ui.table.top.aligned,
.ui.table .top.aligned {
  vertical-align: top;
}
.ui.table.middle.aligned,
.ui.table .middle.aligned {
  vertical-align: middle;
}
.ui.table.bottom.aligned,
.ui.table .bottom.aligned{
  vertical-align: bottom;
}

.ui.label, .ui.label div {
    display: inline-block;
    margin: 0.5em;
}
</style>
`);
	w.document.title = title;
	w.print();
	if (close) {
		w.close();
	}
};

export default CustomTable;
